import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalCloseButton,
  ModalTitle,
} from '@raptormaps/modal';
import { Button } from '@raptormaps/button';
import { TextInput } from '@raptormaps/text-input';
import { SectionHeader } from '../SectionHeader';
import { InputWithSuffix } from '../InputWithSuffix';
import { SolarFarm } from '../../api/schemas/SolarFarmSchema';
import { UpdateSolarFarmPayload } from '../../api/services/solar-farm/solar-farm.schema';
import { tsecsToDayString, dayStringToTsecs } from '../../utils/general_utils';
import { STATUS_OPTIONS } from './constants';
import { Icon } from '@raptormaps/icon';
import './EditFarmModal.css';

interface EditFarmModalProps {
  farm: SolarFarm | null;
  open: boolean;
  onClose: () => void;
  onSave: (payload: UpdateSolarFarmPayload) => Promise<void>;
}

export function EditFarmModal({
  farm,
  open,
  onClose,
  onSave,
}: EditFarmModalProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [status, setStatus] = useState('');
  const [commissioningDate, setCommissioningDate] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [size, setSize] = useState('');
  const [ppaRate, setPpaRate] = useState('');
  const [peakSunlight, setPeakSunlight] = useState('');
  const [moduleLayout, setModuleLayout] = useState('');
  const [moduleMount, setModuleMount] = useState('');
  const [siteLayout, setSiteLayout] = useState('');
  const [assetId, setAssetId] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (open && farm) {
      setName(farm.name ?? '');
      setAddress(farm.address ?? '');
      setCountry(farm.country ?? '');
      setState(farm.state ?? '');
      setStatus(farm.status != null ? String(farm.status) : '');
      setCommissioningDate(tsecsToDayString(farm.commissioning_tsecs));
      setLatitude(farm.latitude != null ? String(farm.latitude) : '');
      setLongitude(farm.longitude != null ? String(farm.longitude) : '');
      setSize(farm.size != null ? String(farm.size) : '');
      setPpaRate(farm.ppa_rate != null ? String(farm.ppa_rate) : '');
      setPeakSunlight(
        farm.peak_sunlight_hours_annual != null
          ? String(farm.peak_sunlight_hours_annual)
          : '',
      );
      setModuleLayout(farm.module_layout ?? '');
      setModuleMount(farm.module_mount ?? '');
      setSiteLayout(farm.site_layout ?? '');
      setAssetId(farm.asset_id ?? '');
      setAccessCode(farm.access_code ?? '');
      setSaveError(null);
    }
  }, [open, farm]);

  const latNum = Number(latitude);
  const lngNum = Number(longitude);
  const latIsValid =
    latitude === '' || (!isNaN(latNum) && latNum >= -90 && latNum <= 90);
  const lngIsValid =
    longitude === '' || (!isNaN(lngNum) && lngNum >= -180 && lngNum <= 180);
  const sizeIsValid = size === '' || !isNaN(Number(size));
  const ppaRateIsValid = ppaRate === '' || !isNaN(Number(ppaRate));
  const peakSunlightIsValid =
    peakSunlight === '' || !isNaN(Number(peakSunlight));
  const hasValidationError =
    !latIsValid ||
    !lngIsValid ||
    !sizeIsValid ||
    !ppaRateIsValid ||
    !peakSunlightIsValid;

  const handleSave = async () => {
    if (hasValidationError) return;
    setSaving(true);
    setSaveError(null);
    try {
      const raw: UpdateSolarFarmPayload = {
        name: name || null,
        address: address || null,
        country: country || null,
        state: state || null,
        status: status !== '' ? Number(status) : null,
        commissioning_tsecs: dayStringToTsecs(commissioningDate),
        latitude: latitude !== '' ? Number(latitude) : null,
        longitude: longitude !== '' ? Number(longitude) : null,
        size: size !== '' ? Number(size) : null,
        ppa_rate: ppaRate !== '' ? Number(ppaRate) : null,
        peak_sunlight_hours_annual:
          peakSunlight !== '' ? Number(peakSunlight) : null,
        module_layout: moduleLayout || null,
        module_mount: moduleMount || null,
        site_layout: siteLayout || null,
        asset_id: assetId || null,
        access_code: accessCode || null,
      };
      const payload = Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v !== null),
      ) as UpdateSolarFarmPayload;
      await onSave(payload);
    } catch (err) {
      console.error('Failed to save farm details:', err);
      setSaveError('Failed to save farm details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const farmId = farm?.id != null ? String(farm.id) : '—';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent>
        <ModalTitle className="efd-sr-only">Edit Farm Details</ModalTitle>
        <div className="efd-root">
          <div className="efd-header">
            <div className="efd-header-left">
              <span className="efd-header-icon" aria-hidden>
                <Icon icon="SolarPanel" />
              </span>
              <span className="efd-header-title">
                Edit Farm Details
                {farm?.name && (
                  <>
                    {' '}
                    — <span className="efd-header-farm-name">{farm.name}</span>
                  </>
                )}
              </span>
            </div>
            <ModalCloseButton />
          </div>

          <div className="efd-body">
            <SectionHeader>Identification</SectionHeader>
            <div className="efd-card-group efd-grid-3">
              <TextInput
                label="Farm Name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Farm name"
              />
              <TextInput
                label="Internal ID"
                value={farmId}
                readOnly
                tabIndex={-1}
              />
              <TextInput
                label="External ID (Asset ID)"
                value={assetId}
                onChange={e => setAssetId(e.target.value)}
                placeholder="External asset identifier"
              />
              <TextInput
                label="Access Code"
                value={accessCode}
                onChange={e => setAccessCode(e.target.value)}
                placeholder="Access code"
              />
              <div className="efd-field">
                <label className="efd-label" htmlFor="farm-status">
                  Status
                </label>
                <select
                  id="farm-status"
                  className="efd-select"
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                >
                  <option value="">Select status</option>
                  {STATUS_OPTIONS.filter(opt => opt.value !== '').map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <SectionHeader>Location Details</SectionHeader>
            <div className="efd-card-group--col">
              <div className="efd-grid-3">
                <div style={{ gridColumn: 'span 2' }}>
                  <TextInput
                    label="Street Address"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Full street address"
                  />
                </div>
                <TextInput
                  label="Country"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  placeholder="e.g. United States"
                />
              </div>
              <div className="efd-grid-4">
                <TextInput
                  label="State / Province"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  placeholder="e.g. Arizona"
                />
                <div className="efd-field">
                  <label className="efd-label" htmlFor="commissioning-date">
                    Commissioning Date
                  </label>
                  <input
                    id="commissioning-date"
                    className="efd-input"
                    type="date"
                    value={commissioningDate}
                    onChange={e => setCommissioningDate(e.target.value)}
                  />
                </div>
                <TextInput
                  label="Latitude"
                  value={latitude}
                  onChange={e => setLatitude(e.target.value)}
                  placeholder="33.4484"
                  error={!latIsValid}
                  helperText={
                    !latIsValid ? 'Must be between -90 and 90' : undefined
                  }
                />
                <TextInput
                  label="Longitude"
                  value={longitude}
                  onChange={e => setLongitude(e.target.value)}
                  placeholder="-112.0740"
                  error={!lngIsValid}
                  helperText={
                    !lngIsValid ? 'Must be between -180 and 180' : undefined
                  }
                />
              </div>
            </div>

            <div className="efd-two-col">
              <div className="efd-col">
                <SectionHeader>Site Specifics</SectionHeader>
                <div className="efd-card-group--col">
                  <div className="efd-field">
                    <label className="efd-label">PPA Rate</label>
                    <InputWithSuffix
                      value={ppaRate}
                      onChange={setPpaRate}
                      placeholder="0.082"
                      suffix="$/kWh"
                      hasError={!ppaRateIsValid}
                    />
                    {!ppaRateIsValid && (
                      <span className="efd-error">Must be a number</span>
                    )}
                  </div>
                  <div className="efd-field">
                    <label className="efd-label">Peak Sunlight</label>
                    <InputWithSuffix
                      value={peakSunlight}
                      onChange={setPeakSunlight}
                      placeholder="1.0"
                      suffix="hrs/day"
                      hasError={!peakSunlightIsValid}
                    />
                    {!peakSunlightIsValid && (
                      <span className="efd-error">Must be a number</span>
                    )}
                  </div>
                  <div className="efd-field">
                    <label className="efd-label">Capacity</label>
                    <InputWithSuffix
                      value={size}
                      onChange={setSize}
                      placeholder="20"
                      suffix="MW"
                      hasError={!sizeIsValid}
                    />
                    {!sizeIsValid && (
                      <span className="efd-error">Must be a number</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="efd-col">
                <SectionHeader>Module Configuration</SectionHeader>
                <div className="efd-card-group--col">
                  <TextInput
                    label="Module Layout"
                    value={moduleLayout}
                    onChange={e => setModuleLayout(e.target.value)}
                    placeholder="e.g. portrait"
                  />
                  <TextInput
                    label="Mounting System"
                    value={moduleMount}
                    onChange={e => setModuleMount(e.target.value)}
                    placeholder="e.g. ground"
                  />
                  <TextInput
                    label="Site Layout"
                    value={siteLayout}
                    onChange={e => setSiteLayout(e.target.value)}
                    placeholder="e.g. fixed_tilt"
                  />
                </div>
              </div>
            </div>

            {saveError && <div className="efd-save-error">{saveError}</div>}
          </div>

          <div className="efd-footer">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || hasValidationError}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
